import { IssueEntity, IssueState } from '@/types';
import {
    FETCH_ISSUES_SUCCESS,
    FetchIssuesSuccessAction,
    ISSUE_FETCHED,
    ISSUE_UPDATED,
    IssueFetchedAction,
    IssueUpdatedAction,
} from '@/actions/triage';

const CLOSED_ISSUE_STATES = [IssueState.FixMerged, IssueState.FixRejected, IssueState.NotAPriority];

// Open/closed bucket a state belongs to — drives count adjustments and tab membership below.
const issueStatusGroup = (state: IssueState): 'open' | 'closed' =>
    CLOSED_ISSUE_STATES.includes(state) ? 'closed' : 'open';

export interface IssuesStateForApp {
    issues: IssueEntity[];
    openCount: number;
    closedCount: number;
    // Deep-linked issues fetched on demand (may not belong to the active tab/list)
    coldLoadedById?: Record<string, IssueEntity>;
}

export type IssuesByAppId = Record<string, IssuesStateForApp>;

const emptyForApp: IssuesStateForApp = { issues: [], openCount: 0, closedCount: 0 };

type IssuesAction = FetchIssuesSuccessAction | IssueUpdatedAction | IssueFetchedAction;

export function issuesReducer(state: IssuesByAppId = {}, action: IssuesAction): IssuesByAppId {
    switch (action.type) {
        case FETCH_ISSUES_SUCCESS: {
            const { appId, issues, offset, openCount, closedCount } = action.payload;
            // Preserve any cold-loaded (deep-linked) issues across list (re)fetches so the route-driven drawer
            // doesn't lose its issue when the list finishes loading.
            const coldLoadedById = state[appId]?.coldLoadedById;
            if (offset === 0) {
                // First page (or filter change): replace
                return { ...state, [appId]: { issues, openCount, closedCount, coldLoadedById } };
            }
            // Subsequent page: append issues (dedup by Id, latest wins), refresh counts.
            const existing = state[appId] ?? emptyForApp;
            const byId = new Map<string, IssueEntity>();
            for (const issue of existing.issues) byId.set(issue.id, issue);
            for (const issue of issues) byId.set(issue.id, issue);
            return {
                ...state,
                [appId]: { issues: Array.from(byId.values()), openCount, closedCount, coldLoadedById },
            };
        }
        case ISSUE_UPDATED: {
            const { appId, issue, statusGroup } = action.payload;
            const existing = state[appId] ?? emptyForApp;
            const previous = existing.issues.find((i) => i.id === issue.id);

            // Adjust open/closed totals when the state change crosses buckets.
            let { openCount, closedCount } = existing;
            if (previous) {
                const from = issueStatusGroup(previous.state);
                const to = issueStatusGroup(issue.state);
                if (from !== to) {
                    if (from === 'open') openCount = Math.max(0, openCount - 1);
                    else closedCount = Math.max(0, closedCount - 1);
                    if (to === 'open') openCount += 1;
                    else closedCount += 1;
                }
            }

            // Drop the issue from the list when it no longer belongs to the active tab; else replace in place.
            const stillInTab = !statusGroup || issueStatusGroup(issue.state) === statusGroup;
            const issues =
                stillInTab ?
                    existing.issues.map((i) => (i.id === issue.id ? issue : i))
                :   existing.issues.filter((i) => i.id !== issue.id);

            // Keep a cold-loaded copy (if any) fresh too, so the route-driven drawer reflects the update.
            const coldLoadedById =
                existing.coldLoadedById?.[issue.id] ?
                    { ...existing.coldLoadedById, [issue.id]: issue }
                :   existing.coldLoadedById;

            return { ...state, [appId]: { issues, openCount, closedCount, coldLoadedById } };
        }
        case ISSUE_FETCHED: {
            const { appId, issue } = action.payload;
            const existing = state[appId] ?? emptyForApp;
            // If the issue is already in the active list, refresh it there; otherwise stash it as cold-loaded.
            if (existing.issues.some((i) => i.id === issue.id)) {
                return {
                    ...state,
                    [appId]: { ...existing, issues: existing.issues.map((i) => (i.id === issue.id ? issue : i)) },
                };
            }
            return {
                ...state,
                [appId]: { ...existing, coldLoadedById: { ...(existing.coldLoadedById ?? {}), [issue.id]: issue } },
            };
        }
        default:
            return state;
    }
}

export default issuesReducer;
